import { useState, useRef, useCallback, useEffect } from 'react';
import * as Tone from 'tone';
import { Chord, Note } from 'tonal';

// Sample URLs from Tone.js project
const PIANO_SAMPLES_URL = 'https://tonejs.github.io/audio/salamander/';
const GUITAR_SAMPLES_URL = 'https://gleitz.github.io/midi-js-soundfonts/FluidR3_GM/acoustic_guitar_nylon-mp3/';

// Notes to load for sampler (subset for faster loading)
const PIANO_NOTES = {
  'C3': 'C3.mp3', 'D#3': 'Ds3.mp3', 'F#3': 'Fs3.mp3', 'A3': 'A3.mp3',
  'C4': 'C4.mp3', 'D#4': 'Ds4.mp3', 'F#4': 'Fs4.mp3', 'A4': 'A4.mp3',
  'C5': 'C5.mp3',
};

const GUITAR_NOTES = {
  'E2': 'E2.mp3', 'A2': 'A2.mp3', 'C3': 'C3.mp3', 'D3': 'D3.mp3',
  'E3': 'E3.mp3', 'G3': 'G3.mp3', 'A3': 'A3.mp3', 'B3': 'B3.mp3',
  'C4': 'C4.mp3', 'D4': 'D4.mp3', 'E4': 'E4.mp3',
};

/**
 * Convert a chord name to an array of MIDI-friendly note strings (e.g. ["A3", "C4", "E4"]).
 * Uses Tonal.js for chord-to-notes, then assigns octaves for a natural voicing.
 */
function chordToNotes(chordName) {
  if (!chordName) return [];

  const parsed = Chord.get(chordName);
  if (!parsed.notes || parsed.notes.length === 0) {
    // Fallback: try just the root note
    const rootMatch = chordName.match(/^([A-G][#b]?)/);
    if (rootMatch) return [rootMatch[1] + '3'];
    return [];
  }

  // Voice the chord: root at octave 3, rest closest above
  const notes = parsed.notes;
  const result = [notes[0] + '3'];

  for (let i = 1; i < notes.length; i++) {
    // Find closest octave above or at the root
    const rootMidi = Note.midi(notes[0] + '3');
    let noteMidi = Note.midi(notes[i] + '3');
    if (noteMidi !== null && rootMidi !== null) {
      while (noteMidi < rootMidi) noteMidi += 12;
      // Keep within a reasonable range
      if (noteMidi > rootMidi + 24) noteMidi -= 12;
      result.push(Note.fromMidi(noteMidi));
    } else {
      result.push(notes[i] + '4');
    }
  }

  return result;
}

/**
 * Custom hook for chord playback using Tone.js.
 *
 * @param {string[]} chordSequence - Ordered array of chord symbols
 * @param {Object} options
 * @param {number} options.defaultBpm - Default BPM (default: 100)
 * @param {function} options.onChordChange - Callback when active chord changes
 * @returns {Object} Player controls and state
 */
export function useChordPlayer(chordSequence, { defaultBpm = 100, onChordChange } = {}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [bpm, setBpmState] = useState(defaultBpm);
  const [volume, setVolumeState] = useState(0.7);
  const [instrument, setInstrumentState] = useState('piano'); // 'piano' | 'guitar'

  const pianoRef = useRef(null);
  const guitarRef = useRef(null);
  const scheduledIdRef = useRef(null);
  const currentIndexRef = useRef(-1);
  const chordSequenceRef = useRef(chordSequence);
  const onChordChangeRef = useRef(onChordChange);
  const volumeNodeRef = useRef(null);
  const instrumentRef = useRef('piano');
  const bpmRef = useRef(defaultBpm);
  const isPlayingRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { chordSequenceRef.current = chordSequence; }, [chordSequence]);
  useEffect(() => { onChordChangeRef.current = onChordChange; }, [onChordChange]);
  useEffect(() => { instrumentRef.current = instrument; }, [instrument]);
  useEffect(() => { bpmRef.current = bpm; }, [bpm]);

  // Initialize volume node
  useEffect(() => {
    volumeNodeRef.current = new Tone.Volume(-6).toDestination();
    return () => {
      volumeNodeRef.current?.dispose();
    };
  }, []);

  // Load piano sampler on mount
  useEffect(() => {
    if (!volumeNodeRef.current) return;

    const urls = {};
    for (const [note, file] of Object.entries(PIANO_NOTES)) {
      urls[note] = file;
    }
    pianoRef.current = new Tone.Sampler({
      urls,
      baseUrl: PIANO_SAMPLES_URL,
      onload: () => setIsLoaded(true),
      onerror: (err) => {
        console.error('Piano samples failed to load:', err);
        setIsLoaded(true); // unblock UI even if samples fail
      },
      release: 1.5,
    }).connect(volumeNodeRef.current);

    return () => {
      pianoRef.current?.dispose();
    };
  }, []);

  // Load guitar sampler lazily
  const [guitarLoaded, setGuitarLoaded] = useState(false);
  const loadGuitar = useCallback(() => {
    if (guitarRef.current || !volumeNodeRef.current) return;
    const urls = {};
    for (const [note, file] of Object.entries(GUITAR_NOTES)) {
      urls[note] = file;
    }
    // Guitar samples are quieter than piano — boost by +12dB
    const guitarGain = new Tone.Gain(4).connect(volumeNodeRef.current);
    guitarRef.current = new Tone.Sampler({
      urls,
      baseUrl: GUITAR_SAMPLES_URL,
      onload: () => {
        setGuitarLoaded(true);
        console.log('Guitar samples loaded');
      },
      onerror: (err) => {
        console.error('Guitar samples failed to load:', err);
        setGuitarLoaded(true); // unblock UI
      },
      release: 2,
    }).connect(guitarGain);
  }, []);

  // Get active sampler
  const getSampler = useCallback(() => {
    if (instrumentRef.current === 'guitar' && guitarRef.current?.loaded) {
      return guitarRef.current;
    }
    return pianoRef.current;
  }, []);

  // Play a single chord
  const playChord = useCallback((chordName) => {
    const sampler = getSampler();
    if (!sampler || !sampler.loaded) return;

    const notes = chordToNotes(chordName);
    if (notes.length === 0) return;

    // Release previous notes
    sampler.releaseAll();
    // Play new chord
    const duration = (60 / bpmRef.current) * 2; // 2 beats
    sampler.triggerAttackRelease(notes, duration);
  }, [getSampler]);

  // Schedule next chord
  const scheduleNext = useCallback(() => {
    if (!isPlayingRef.current) return;

    const seq = chordSequenceRef.current;
    const nextIdx = currentIndexRef.current + 1;

    if (nextIdx >= seq.length) {
      // Reached end — stop
      setIsPlaying(false);
      isPlayingRef.current = false;
      return;
    }

    currentIndexRef.current = nextIdx;
    setCurrentIndex(nextIdx);
    onChordChangeRef.current?.(nextIdx);
    playChord(seq[nextIdx]);

    // Schedule next
    const intervalMs = ((60 / bpmRef.current) * 2) * 1000; // 2 beats in ms
    scheduledIdRef.current = setTimeout(scheduleNext, intervalMs);
  }, [playChord]);

  const play = useCallback(async () => {
    if (chordSequenceRef.current.length === 0) return;

    // Start audio context (browser restriction)
    await Tone.start();

    isPlayingRef.current = true;
    setIsPlaying(true);

    // If at end or not started, start from beginning
    if (currentIndexRef.current >= chordSequenceRef.current.length - 1 || currentIndexRef.current < 0) {
      currentIndexRef.current = -1;
    }

    // Play immediately
    scheduleNext();
  }, [scheduleNext]);

  const pause = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    if (scheduledIdRef.current) {
      clearTimeout(scheduledIdRef.current);
      scheduledIdRef.current = null;
    }
    getSampler()?.releaseAll();
  }, [getSampler]);

  const stop = useCallback(() => {
    pause();
    currentIndexRef.current = -1;
    setCurrentIndex(-1);
    onChordChangeRef.current?.(-1);
  }, [pause]);

  const skipForward = useCallback(() => {
    const seq = chordSequenceRef.current;
    if (currentIndexRef.current >= seq.length - 1) return;

    if (scheduledIdRef.current) {
      clearTimeout(scheduledIdRef.current);
      scheduledIdRef.current = null;
    }

    const nextIdx = currentIndexRef.current + 1;
    currentIndexRef.current = nextIdx;
    setCurrentIndex(nextIdx);
    onChordChangeRef.current?.(nextIdx);

    if (isPlayingRef.current) {
      playChord(seq[nextIdx]);
      const intervalMs = ((60 / bpmRef.current) * 2) * 1000;
      scheduledIdRef.current = setTimeout(scheduleNext, intervalMs);
    }
  }, [playChord, scheduleNext]);

  const skipBackward = useCallback(() => {
    if (currentIndexRef.current <= 0) return;

    if (scheduledIdRef.current) {
      clearTimeout(scheduledIdRef.current);
      scheduledIdRef.current = null;
    }

    const prevIdx = currentIndexRef.current - 1;
    currentIndexRef.current = prevIdx;
    setCurrentIndex(prevIdx);
    onChordChangeRef.current?.(prevIdx);

    if (isPlayingRef.current) {
      playChord(chordSequenceRef.current[prevIdx]);
      const intervalMs = ((60 / bpmRef.current) * 2) * 1000;
      scheduledIdRef.current = setTimeout(scheduleNext, intervalMs);
    }
  }, [playChord, scheduleNext]);

  const seekTo = useCallback((index) => {
    const seq = chordSequenceRef.current;
    if (index < 0 || index >= seq.length) return;

    if (scheduledIdRef.current) {
      clearTimeout(scheduledIdRef.current);
      scheduledIdRef.current = null;
    }

    currentIndexRef.current = index;
    setCurrentIndex(index);
    onChordChangeRef.current?.(index);

    if (isPlayingRef.current) {
      playChord(seq[index]);
      const intervalMs = ((60 / bpmRef.current) * 2) * 1000;
      scheduledIdRef.current = setTimeout(scheduleNext, intervalMs);
    }
  }, [playChord, scheduleNext]);

  const setBpm = useCallback((newBpm) => {
    const clamped = Math.max(40, Math.min(200, newBpm));
    setBpmState(clamped);
    bpmRef.current = clamped;
  }, []);

  const setVolume = useCallback((vol) => {
    const clamped = Math.max(0, Math.min(1, vol));
    setVolumeState(clamped);
    if (volumeNodeRef.current) {
      // Convert 0-1 to dB (-60 to 0)
      volumeNodeRef.current.volume.value = clamped === 0 ? -Infinity : -60 + (clamped * 60);
    }
  }, []);

  const setInstrument = useCallback((type) => {
    if (type === 'guitar') {
      loadGuitar();
    }
    setInstrumentState(type);
  }, [loadGuitar]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scheduledIdRef.current) clearTimeout(scheduledIdRef.current);
      pianoRef.current?.dispose();
      guitarRef.current?.dispose();
    };
  }, []);

  // Reset when chord sequence changes (e.g. transpose or song change)
  useEffect(() => {
    if (isPlayingRef.current) {
      // Keep playing but from current position if valid
      if (currentIndexRef.current >= chordSequence.length) {
        stop();
      }
    }
  }, [chordSequence, stop]);

  return {
    isPlaying,
    isLoaded,
    currentIndex,
    play,
    pause,
    stop,
    skipForward,
    skipBackward,
    seekTo,
    setBpm,
    setVolume,
    setInstrument,
    bpm,
    volume,
    instrument,
  };
}
